// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { GetCustomQuotesApiParameter } from "@ces/App/Handlers/RfqMyCustomQuotes/UpdateSearchFields";
import RfqMyCustomQuotesState from "@ces/App/Store/Selectors/RfqMyCustomQuotesState";
import { createTypedReducerWithImmer } from "@insite/client-framework/Common/CreateTypedReducer";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { AnyAction, reducers } from "@insite/client-framework/Store/Reducers";
import { UpdateSearchFieldsType } from "@insite/client-framework/Types/UpdateSearchFieldsType";
import { Draft } from "immer";

const initialState: RfqMyCustomQuotesState = {
    getCustomQuotesParameter: {
        sort: "enteredDate DESC, invoiceNumber DESC",
    },
};

const reducer = {
    "Pages/RfqMyCustomQuotes/UpdateSearchFields": (
        draft: Draft<RfqMyCustomQuotesState>,
        action: { parameter: GetCustomQuotesApiParameter & UpdateSearchFieldsType },
    ) => {
        const { type } = action.parameter;
        delete action.parameter.type;
        if (type === "Replace") {
            draft.getCustomQuotesParameter = action.parameter;
        } else if (type === "Initialize") {
            draft.getCustomQuotesParameter = { ...initialState.getCustomQuotesParameter, ...action.parameter };
        } else {
            draft.getCustomQuotesParameter = { ...draft.getCustomQuotesParameter, ...action.parameter };

            for (const key in draft.getCustomQuotesParameter) {
                const value = (<any>draft.getCustomQuotesParameter)[key];

                // remove empty parameters
                if (value === "" || value === undefined) {
                    delete (<any>draft.getCustomQuotesParameter)[key];
                }
            }

            for (const key in action.parameter) {
                // go back to page 1 if any other parameters changed
                if (
                    draft.getCustomQuotesParameter.page &&
                    draft.getCustomQuotesParameter.page > 1 &&
                    key !== "page" &&
                    key !== "pageSize"
                ) {
                    draft.getCustomQuotesParameter.page = 1;
                }
            }
        }
    },
    "Pages/RfqMyCustomQuotes/ClearParameter": (draft: Draft<RfqMyCustomQuotesState>) => {
        draft.getCustomQuotesParameter = {
            ...initialState.getCustomQuotesParameter,
            pageSize: draft.getCustomQuotesParameter.pageSize,
        };
    },
};

const xRfqMyQuotes = createTypedReducerWithImmer(initialState, reducer);

(reducers as any).xRfqMyQuotes = xRfqMyQuotes;

export type xRfqMyCustomQuotesState = {
    xRfqMyQuotes: RfqMyCustomQuotesState;
} & ApplicationState;

type xRfqMyQuotesReducers = {
    [key: string]: typeof xRfqMyQuotes;
};

export type xRfqMyQuotesActions = Parameters<xRfqMyQuotesReducers[keyof xRfqMyQuotesReducers]>[1] | AnyAction;
